import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ptBR from './locales/pt-BR.json';
import es from './locales/es.json';

const SUPPORTED_LANGUAGES = ['en', 'pt', 'pt-BR', 'es'];

export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português (PT)' },
  { value: 'pt-BR', label: 'Português (BR)' },
  { value: 'es', label: 'Español' },
];

export const isSupportedLanguage = (lang: unknown): lang is string =>
  typeof lang === 'string' && SUPPORTED_LANGUAGES.includes(lang);

// Switch the UI language and remember it in the local app settings
export const applyLanguage = (lang: string) => {
  if (!isSupportedLanguage(lang)) return;
  try {
    const settings = JSON.parse(localStorage.getItem('app_settings') || '{}');
    localStorage.setItem('app_settings', JSON.stringify({ ...settings, language: lang }));
  } catch (e) {
    // Ignore storage errors
  }
  i18n.changeLanguage(lang);
};

const resources = {
  en: { translation: en },
  pt: { translation: ptBR },
  'pt-BR': { translation: ptBR },
  es: { translation: es },
};

// Get saved language from settings or fallback to browser language
const getSavedLanguage = (): string => {
  try {
    const settings = localStorage.getItem('app_settings');
    if (settings) {
      const parsed = JSON.parse(settings);
      if (parsed.language && SUPPORTED_LANGUAGES.includes(parsed.language)) {
        return parsed.language;
      }
    }
  } catch (e) {
    // Ignore parsing errors
  }
  
  // Fallback to browser language (check full locale first, then base)
  const browserLang = navigator.language;
  if (SUPPORTED_LANGUAGES.includes(browserLang)) return browserLang;
  const baseLang = browserLang.split('-')[0];
  return SUPPORTED_LANGUAGES.includes(baseLang) ? baseLang : 'en';
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

// Keep <html lang> in sync with the active language
document.documentElement.lang = i18n.language;
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
